import { Inbox, Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { testIds } from '@/lib/test-ids'
import { getConversation } from '@/lib/api'
import { getFlowRuns } from '@/lib/api/flows'
import { useEffect, useState } from 'react'

export type FlowAgentOrigin = {
  flowId: string | null
  runId: string | null
  inboxThreadId: string | null
}

function asUuid(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
    ? trimmed
    : null
}

/** Resolve flow / inbox origin from a persisted flow-agent conversation. */
export async function resolveFlowAgentOrigin(conversationId: string): Promise<FlowAgentOrigin | null> {
  const result = await getConversation(conversationId, false)
  const meta = result.conversation?.metadata || {}
  if (meta.source !== 'flow_agent_harness' && meta.step !== 'ai.agent_session') {
    return null
  }
  const flowId = asUuid(meta.flow_id)
  const runId = asUuid(meta.run_id)
  let inboxThreadId = asUuid(meta.output_thread_id)
  if (!inboxThreadId && flowId && runId) {
    try {
      const runs = await getFlowRuns(flowId)
      inboxThreadId = runs.find((run) => run.id === runId)?.output_thread_id ?? null
    } catch {
      inboxThreadId = null
    }
  }
  if (!flowId && !inboxThreadId) return null
  return { flowId, runId, inboxThreadId }
}

type FlowAgentOriginBarProps = {
  origin: FlowAgentOrigin
  onOpenFlow?: (flowId: string) => void
}

/** Compact links from a flow-agent chat back to Inbox and the flow document. */
export function FlowAgentOriginBar({ origin, onOpenFlow }: FlowAgentOriginBarProps) {
  const hasInbox = Boolean(origin.inboxThreadId)
  const hasFlow = Boolean(origin.flowId)
  if (!hasInbox && !hasFlow) return null

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-1.5"
      data-testid={testIds.chatPane.flowAgentOrigin}
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Flow agent
      </span>
      {hasInbox ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          data-testid={testIds.chatPane.flowAgentViewInbox}
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('open-inbox-thread-main', {
                detail: {
                  threadId: origin.inboxThreadId,
                  title: 'Flow output',
                },
              }),
            )
          }}
        >
          <Inbox className="mr-1 h-3 w-3" />
          View in Inbox
        </Button>
      ) : null}
      {hasFlow ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          data-testid={testIds.chatPane.flowAgentViewFlow}
          onClick={() => onOpenFlow?.(origin.flowId as string)}
        >
          <Workflow className="mr-1 h-3 w-3" />
          View flow
        </Button>
      ) : null}
    </div>
  )
}

/** Load origin for the active conversation; null when this is a normal user chat. */
export function useFlowAgentOrigin(conversationId: string | null | undefined) {
  const [origin, setOrigin] = useState<FlowAgentOrigin | null>(null)

  useEffect(() => {
    if (!conversationId) {
      setOrigin(null)
      return
    }
    let cancelled = false
    void resolveFlowAgentOrigin(conversationId)
      .then((next) => {
        if (!cancelled) setOrigin(next)
      })
      .catch(() => {
        if (!cancelled) setOrigin(null)
      })
    return () => {
      cancelled = true
    }
  }, [conversationId])

  return origin
}
